// The keybind MODEL, lifted out of InputService so it's testable without the DOM (Set 12.2). A
// binding is action -> [keyCode]; an action can have several keys (move has KeyW/ArrowUp/Space).
// Defaults come from data/controls.json; the player's overrides layer on top and persist in
// localStorage. A CONFLICT is one key bound to two+ actions — the settings menu paints those red and
// names the clash. Everything here is pure except the two localStorage helpers at the bottom.

// action -> [keys], from controls.json (move is key->action, many keys per action; actions is
// action->key). This is the baseline the player's overrides layer over.
export function defaultBindings(controls) {
    const b = {};
    for (const [key, action] of Object.entries(controls.move || {})) (b[action] ||= []).push(key);
    for (const [action, key] of Object.entries(controls.actions || {})) (b[action] ||= []).push(key);
    return b;
}

// Layer the player's saved overrides (a partial action -> [keys]) over the defaults. An override
// wholly replaces that action's key list; actions the player never touched keep their defaults.
export function mergeBindings(defaults, overrides = {}) {
    const out = {};
    for (const a of new Set([...Object.keys(defaults), ...Object.keys(overrides)])) {
        out[a] = (overrides[a] ?? defaults[a] ?? []).slice();
    }
    return out;
}

// key -> [actions] for every key bound to more than one action. Empty object == no conflicts. This is
// what the menu reads to flag a row red and say which other action holds the key.
export function conflicts(bindings) {
    const byKey = {};
    for (const [action, keys] of Object.entries(bindings)) for (const k of keys) (byKey[k] ||= []).push(action);
    const out = {};
    for (const [k, acts] of Object.entries(byKey)) if (acts.length > 1) out[k] = acts;
    return out;
}

// key -> action, for the input dispatch (which action a keydown fires). On a conflicted key the first
// action bound to it wins the dispatch; the menu is where the player is nudged to resolve the clash.
export function keymap(bindings) {
    const m = {};
    for (const [action, keys] of Object.entries(bindings)) for (const k of keys) if (!(k in m)) m[k] = action;
    return m;
}

// Return new bindings with `key` bound to `action`. replace=true swaps the action's whole key list
// (the common rebind); replace=false appends a second key. Never mutates the input.
export function setBinding(bindings, action, key, replace = true) {
    const next = { ...bindings };
    next[action] = replace ? [key] : [...(bindings[action] || []), key];
    return next;
}

// The overrides that DIFFER from the defaults — what we actually persist, so a later change to the
// default set still reaches anyone who hasn't rebound that action.
export function diffFromDefaults(bindings, defaults) {
    const ov = {};
    for (const [a, keys] of Object.entries(bindings)) {
        const d = defaults[a] || [];
        if (keys.length !== d.length || keys.some((k, i) => k !== d[i])) ov[a] = keys.slice();
    }
    return ov;
}

// --- persistence (the only non-pure part) ------------------------------------------------
const STORE_KEY = "mw:keybinds";
export function loadOverrides() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY) || "{}"); } catch { return {}; }
}
export function saveOverrides(overrides) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(overrides)); } catch { /* private mode: never mind */ }
}
